import { createWithEqualityFn } from 'zustand/traditional';

import { School } from '@common-types';

import { trackEvent } from '@utils/googleAnalytics';
import { getSchoolType } from '@utils/schoolInfographics';

import { addSchoolFavorite, deleteSchoolFavorite } from '@api/userPreferences';

import useNotificationStore from '@store/NotificationStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

export interface SchoolFavoriteStore {
  isLoading: boolean;
  isSurveyOpen: boolean;
  setIsSurveyOpen: (isSurveyOpen: boolean) => void;
  toggleSchoolFavorite: (toggleSchool: School) => Promise<void>;
  addSchoolFavorite: (id: number) => Promise<void>;
  removeSchoolFavorite: (id: number) => Promise<void>;
}

const initialState = {
  isLoading: false,
  isSurveyOpen: false,
};

export const useSchoolFavoritesStore = createWithEqualityFn<SchoolFavoriteStore>()(
  (set, get) => ({
    ...initialState,

    setIsSurveyOpen: (isSurveyOpen: boolean) => set({ isSurveyOpen }),

    toggleSchoolFavorite: async (toggleSchool: School) => {
      const state = get();
      const foundSchool = useUserPreferencesStore
        .getState()
        .schoolFavorites?.find((school) => school.id === toggleSchool.id);

      set({ isLoading: true });

      try {
        if (!foundSchool) {
          await state.addSchoolFavorite(toggleSchool.id);
        } else {
          await state.removeSchoolFavorite(toggleSchool.id);
        }
      } finally {
        set({ isLoading: false });
      }

      trackEvent({
        eventType: foundSchool ? 'Unfavorite College' : 'Favorite College',
        props: {
          schoolName: toggleSchool.directory.name,
          schoolId: toggleSchool.id,
          schoolType: getSchoolType(toggleSchool.directory?.demographics.carnegieSizeSetting),
          schoolState: toggleSchool.directory.address.state,
        },
      });
    },

    addSchoolFavorite: async (id: number) => {
      await addSchoolFavorite(id);

      useNotificationStore.getState().notify({
        message: `Saved to Favorites`,
        severity: 'info',
      });
    },

    removeSchoolFavorite: async (id: number) => {
      await deleteSchoolFavorite(id);

      useNotificationStore.getState().notify({
        message: `Removed from Favorites`,
        severity: 'info',
      });
    },
  }),
  Object.is,
);
